import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NewVersionModalComponent } from '@warema/shared/app-version/ui';
import { BaseEnvironment, ENVIRONMENT } from '@warema/shared/models';
import { PopupService } from '@warema/warema-ui-angular';
import { first, lastValueFrom, map } from 'rxjs';

interface CurrentCommitSHA {
    currentReleasedCommitSHA: string;
}

@Injectable({
    providedIn: 'root',
})
export class AppVersionService {
    private readonly _environment = inject<BaseEnvironment>(ENVIRONMENT);
    private readonly _http = inject(HttpClient);
    private readonly _injector = inject(Injector);
    private readonly _translateService = inject(TranslateService);

    private _loadCurrentServerCommitSha(): Promise<string> {
        return lastValueFrom(
            this._http
                .get<CurrentCommitSHA>(`assets/versionInfo.json?cacheKill=${Date.now()}`)
                .pipe(map(x => x.currentReleasedCommitSHA)),
        );
    }

    private async _checkAppVersion(): Promise<void> {
        const currentCommitSha = this._environment.currentReleasedCommitSHA;
        const isInLocal = this._environment.instance.startsWith('local');
        const isInDev = this._environment.instance === 'dev';

        if (isInLocal || isInDev || !currentCommitSha) {
            return;
        }

        const serverCommitSha = await this._loadCurrentServerCommitSha();
        if (serverCommitSha !== currentCommitSha) {
            this._promptUserForUpdate();
        }
    }

    public setupReoccuringAppVersionCheck(intervalTime = 2 * 60 * 1000): void {
        setInterval((): void => void this._checkAppVersion(), intervalTime);
    }

    private _promptUserForUpdate(): void {
        const popupService = this._injector.get(PopupService);

        const openPopupFn = (): void => {
            const dialogRef = popupService.open(
                NewVersionModalComponent,
                {},
                {
                    centerAlignedDimensions: {
                        width: '310px',
                    },
                    size: 'small',
                    force: true,
                    renderCloseButton: false,
                    headline: `${this._translateService.instant('NEW_VERSION_AVAILABLE')}`,
                    closeOnBackdropClick: false,
                },
            );
            dialogRef.afterClosed$.pipe(first()).subscribe(confirmedRefresh => {
                if (confirmedRefresh) {
                    window.location.reload();
                }
            });
        };

        if (this._translateService.getLangs().includes(this._translateService.getCurrentLang())) {
            openPopupFn();
        } else {
            this._translateService.onLangChange.pipe(first()).subscribe(() => openPopupFn());
        }
    }
}
